import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../components/confirm-dialog/confirm-dialog.component';

/**
 * Optimistic concurrency for daily report / daily diary draft saves.
 *
 * The client sends `base_updated_at` (the `updated_at` of the version it loaded). When the
 * record changed since, the api writes nothing and answers
 * 409 `{ status, code: 'report_changed', error, updated_at, updated_by, updated_by_name }`,
 * where `updated_by` is the account id as the api exposes it.
 */
export const REPORT_CHANGED_CODE = 'report_changed';

export interface ReportChangedBody {
  status?: number;
  code?: string;
  error?: string;
  updated_at?: string | null;
  updated_by?: string | null;
  updated_by_name?: string | null;
}

/** The 409 body when `err` (an HttpErrorResponse) is the report_changed conflict, else null. */
export function reportChangedBody(err: unknown): ReportChangedBody | null {
  const { status, error: body } = (err || {}) as { status?: number; error?: unknown };
  if (status === 409 && body && typeof body === 'object' && (body as ReportChangedBody).code === REPORT_CHANGED_CODE) {
    return body as ReportChangedBody;
  }
  return null;
}

/** The parts of the signed-in account used to tell the user's own change from someone else's. */
export interface ConflictUser {
  id?: string | number | null;
  uuid?: string | number | null;
}

/**
 * The `base_updated_at` to send with a draft save: only for a record that already exists
 * (has an id) and whose `updated_at` was loaded. A create sends nothing.
 */
export function baseUpdatedAtFor(
  record: { id?: unknown; uuid?: unknown; updated_at?: unknown } | null | undefined,
): string | null {
  if (!record || !(record.id || record.uuid)) return null;
  const value = record.updated_at;
  if (!value) return null;
  if (value instanceof Date) return value.toISOString();
  return typeof value === 'string' ? value : null;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** True when the conflicting change was made by the signed-in account (e.g. on the mobile app). */
export function changedByCurrentUser(body: ReportChangedBody, currentUser: ConflictUser | null | undefined): boolean {
  const by = body?.updated_by;
  if (by === null || by === undefined || by === '' || !currentUser) return false;
  return [currentUser.id, currentUser.uuid]
    .filter(id => id !== null && id !== undefined && id !== '')
    .some(id => String(id) === String(by));
}

/**
 * Dialog copy for the conflict. `noun` is 'report' for a daily report, 'diary' for a daily diary.
 * The message is rendered as HTML by the confirm dialog, so the name is escaped.
 */
export function reportChangedDialogModel(
  body: ReportChangedBody,
  currentUser: ConflictUser | null | undefined,
  noun: 'report' | 'diary' = 'report',
): ConfirmDialogModel {
  const title = noun === 'diary' ? 'Diary changed' : 'Report changed';
  const tail = 'so your last change wasn\'t saved. Refresh to load the latest version.';
  let message: string;
  if (changedByCurrentUser(body, currentUser)) {
    message = `You changed this ${noun} on the mobile app since you opened it here, ${tail}`;
  } else {
    const name = (body?.updated_by_name || '').trim() || 'another user';
    message = `This ${noun} was changed by ${escapeHtml(name)} since you opened it, ${tail}`;
  }
  return new ConfirmDialogModel(title, message, 'Refresh', 'Cancel');
}

/**
 * Opens the app's confirm dialog for the conflict. Emits true for Refresh, false for Cancel
 * (or the close icon).
 */
export function openReportChangedDialog(
  dialog: MatDialog,
  body: ReportChangedBody,
  currentUser: ConflictUser | null | undefined,
  noun: 'report' | 'diary' = 'report',
): Observable<boolean> {
  return dialog.open(ConfirmDialogComponent, {
    disableClose: true,
    // DESIGN.md: the panelClass rounds the dialog surface to 8px (as risk-detail-panel's confirm does).
    panelClass: 'floating-dialog-box',
    data: reportChangedDialogModel(body, currentUser, noun),
  }).afterClosed();
}
